// 試遊の足あと (配布版にも入る・2026-10-30)。
//
// なぜ要るか (作者 10-30): 「死んだ数と最初に死んだ技を書く紙を添えるってのは、プレイヤーに書かせるって意味かな。
//   それは意味ないというか大変だから、裏でシーンが分かるように記録できるようにしておいてほしい」。
//   = 友達は何も書かない。 遊んでいる間に、 ボスごとの挑戦・死・死因・進み具合・時間をこの端末の localStorage に貯める。
//
// 約束:
//   - 見るだけ。 GameState を書き換えない。
//   - どこへも送らない。 外へ出るのは、 遊んだ人がボタンを押してクリップボードへ写した時だけ
//     (感想欄の「コピーする」・または ?kiroku で開いた時の「記録を写す」)。
//   - 名前・端末・場所など個人を指す物は持たない。 中身はボスの戦績と版だけ。
//   - 開発版の詳しい記録 (src/dev_playlog.ts) とは別物。 あちらは1tick ごとの動き、 こちらは戦績の要約。
//   読み方・集計 = docs/research/2026-10-30-playtest-log.md ・ scripts/playtest_record_summary.mjs

import type { GameState } from './types'

export const PLAYTEST_RECORD_KEY = 'shinige.playtest.record'
export const PLAYTEST_RECORD_VERSION = 1
// 死因の並びは最初の数件だけ残す (最初に死んだ技は firstCause に別途残る)。
const CAUSE_ORDER_MAX = 12

export type BossRecord = {
  name: string
  tries: number          // 戦闘が始まった回数 (再戦を含む)
  deaths: number
  wins: number
  firstCause: string | null
  causes: Record<string, number>
  causeOrder: string[]   // 死んだ順 (先頭 CAUSE_ORDER_MAX 件)
  bestRoom: number       // 届いた一番奥の幕 (1 始まり)
  roomCount: number
  bestBossHpPct: number  // 一番削った時のボス残り体力 % (100 = 無傷)
  fightMs: number        // 戦っていた時間の合計 (playing の間の壁時計)
  firstAt: string        // 初めて挑んだ時刻
}

export type PlaytestRecord = {
  v: number
  build: string
  startedAt: string
  bosses: Record<string, BossRecord>
  order: string[]        // 初めて挑んだ順
}

export function emptyRecord(build: string, now = new Date()): PlaytestRecord {
  return { v: PLAYTEST_RECORD_VERSION, build, startedAt: now.toISOString(), bosses: {}, order: [] }
}

function bossOf(rec: PlaytestRecord, st: GameState, now: Date): BossRecord {
  const id = st.boss.id
  let b = rec.bosses[id]
  if (!b) {
    b = {
      name: st.boss.displayName, tries: 0, deaths: 0, wins: 0, firstCause: null, causes: {}, causeOrder: [],
      bestRoom: 0, roomCount: st.boss.roomCount ?? 1, bestBossHpPct: 100, fightMs: 0, firstAt: now.toISOString(),
    }
    rec.bosses[id] = b
    rec.order.push(id)
  }
  return b
}

function track(b: BossRecord, st: GameState): void {
  b.bestRoom = Math.max(b.bestRoom, (st.boss.executesTaken ?? 0) + 1)
  const pct = st.boss.maxHp > 0 ? Math.round((Math.max(0, st.boss.hp) / st.boss.maxHp) * 100) : 100
  b.bestBossHpPct = Math.min(b.bestBossHpPct, pct)
}

// 1tick 分を足す。 変わった時だけ true (呼び側が保存する)。 rec はその場で書き換える。
export function recordTick(rec: PlaytestRecord, prev: GameState, next: GameState, dtMs: number, now = new Date()): boolean {
  const wasPlaying = prev.phase === 'playing'
  const isPlaying = next.phase === 'playing'
  if (!wasPlaying && !isPlaying) return false
  const b = bossOf(rec, next, now)
  let changed = false
  // 同じ戦いが続いているか (ボスが同じで playing のまま)。 違えば新しい挑戦。
  const sameFight = wasPlaying && prev.boss.id === next.boss.id
  // 開いた瞬間から戦闘中 (?fight=) の時は前の状態も playing なので、 0 回のままにしない。
  if (isPlaying && (!sameFight || b.tries === 0)) { b.tries++; changed = true }
  if (isPlaying) {
    b.fightMs += dtMs
    const room = b.bestRoom, pct = b.bestBossHpPct
    track(b, next)
    if (room !== b.bestRoom || pct !== b.bestBossHpPct) changed = true
  }
  if (wasPlaying && next.phase === 'defeat') {
    const cause = next.lastDeathCause ?? next.lastDeathAttackId ?? '不明'
    b.deaths++
    b.causes[cause] = (b.causes[cause] ?? 0) + 1
    if (!b.firstCause) b.firstCause = cause
    if (b.causeOrder.length < CAUSE_ORDER_MAX) b.causeOrder.push(cause)
    changed = true
  } else if (wasPlaying && (next.phase === 'victory' || next.phase === 'cleared')) {
    b.wins++
    b.bestBossHpPct = 0
    changed = true
  }
  return changed
}

function fmtTime(ms: number): string {
  const s = Math.round(ms / 1000)
  return `${Math.floor(s / 60)}分${String(s % 60).padStart(2, '0')}秒`
}

// 写す文。 人が読めて、 scripts/playtest_record_summary.mjs が行ごとに読める形。
//   1行 = 1ボス。 「【足あと】」で始まる行だけを集計が拾う。
export function formatRecord(rec: PlaytestRecord): string {
  const lines = [`【足あと】版 ${rec.build} ／ 開始 ${rec.startedAt.slice(0, 16).replace('T', ' ')}`]
  for (const id of rec.order) {
    const b = rec.bosses[id]
    if (!b) continue
    const causes = Object.entries(b.causes).sort((x, y) => y[1] - x[1]).map(([k, n]) => `${k}×${n}`).join('、')
    lines.push([
      `【足あと】${b.name}`,
      `挑${b.tries}`, `死${b.deaths}`, `勝${b.wins}`,
      `時間 ${fmtTime(b.fightMs)}`,
      `幕 ${b.bestRoom}/${b.roomCount}`,
      `残体力 ${b.bestBossHpPct}%`,
      `最初の死 ${b.firstCause ?? 'なし'}`,
      `死因 ${causes || 'なし'}`,
      `順 ${b.causeOrder.join('>') || 'なし'}`,
    ].join(' ｜ '))
  }
  return lines.join('\n')
}

// ---- 保存 (localStorage が使えなくても遊びは止めない) ----
export function loadRecord(build: string): PlaytestRecord {
  try {
    const raw = localStorage.getItem(PLAYTEST_RECORD_KEY)
    if (raw) {
      const r = JSON.parse(raw) as PlaytestRecord
      if (r && r.v === PLAYTEST_RECORD_VERSION && r.bosses && Array.isArray(r.order)) {
        r.build = build
        return r
      }
    }
  } catch { /* 壊れていたら作り直す */ }
  return emptyRecord(build)
}
export function saveRecord(rec: PlaytestRecord): void {
  try { localStorage.setItem(PLAYTEST_RECORD_KEY, JSON.stringify(rec)) } catch { /* 保存できなくても遊べる */ }
}

let current: PlaytestRecord | null = null
let lastSaveMs = 0
export function currentRecord(build: string): PlaytestRecord {
  if (!current) current = loadRecord(build)
  return current
}
// main.ts の毎tick から呼ぶ。 区切り (挑戦・死・勝ち・幕) は即保存、 時間だけの変化は 5 秒ごと。
export function playtestTick(prev: GameState, next: GameState, dtMs: number, build: string): void {
  const rec = currentRecord(build)
  const changed = recordTick(rec, prev, next, dtMs)
  const t = Date.now()
  if (changed || (next.phase === 'playing' && t - lastSaveMs > 5000)) { saveRecord(rec); lastSaveMs = t }
}
export function currentRecordText(build: string): string { return formatRecord(currentRecord(build)) }

// ?kiroku で開いた時だけ出す「記録を写す」ボタン (途中でやめた人に、 リンクを1つ渡して1回押してもらう用)。
export function mountRecordCopyButton(build: string): void {
  if (typeof document === 'undefined') return
  const btn = document.createElement('button')
  btn.type = 'button'
  btn.textContent = '遊んだ記録を写す'
  btn.style.cssText = 'position:fixed;right:16px;top:16px;z-index:9999;padding:10px 18px;'
    + 'background:rgba(20,16,28,0.92);color:#e4e2ea;border:1px solid rgba(255,255,255,0.3);border-radius:4px;'
    + 'font:600 15px "Yu Gothic UI","Hiragino Kaku Gothic ProN","Noto Sans JP",sans-serif;cursor:pointer'
  for (const type of ['mousedown', 'mouseup', 'keydown', 'keyup']) btn.addEventListener(type, (e) => e.stopPropagation())
  btn.addEventListener('click', async () => {
    const text = currentRecordText(build)
    try {
      await navigator.clipboard.writeText(text)
      btn.textContent = '写しました。そのまま送ってください'
    } catch {
      window.prompt('Ctrl+C で写してください', text)
    }
  })
  document.body.appendChild(btn)
}
